import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { myApi } from '../../../config/axiosConfig';

import lovesLogo from '../../../assets/img/fuel-prices/loves-logo.png';
import pilotFlyingJLogo from '../../../assets/img/fuel-prices/pilot-flying-j-logo.jpg';
import arguindeguiLogo from '../../../assets/img/fuel-prices/arguindegui-logo.png';
import taLogo from '../../../assets/img/fuel-prices/TA-logo.png';
import eiaLogo from '../../../assets/img/fuel-prices/eia-logo.svg';
import ebtLogo from '../../../assets/img/fuel-prices/ebt-logo.jpg';
import opecLogo from '../../../assets/img/fuel-prices/opec-logo.png';
import gasomaxLogo from '../../../assets/img/fuel-prices/gasomax-logo.png';
import natgasLogo from '../../../assets/img/fuel-prices/natgas-logo.png';
import cugasLogo from '../../../assets/img/fuel-prices/cugas_logo.jpg';
import gnvLogo from '../../../assets/img/fuel-prices/gnv-logo.jpg';
import ergLogo from '../../../assets/img/fuel-prices/erg-logo.png';
import ebtLogo2 from '../../../assets/img/fuel-prices/ebt-logo2.jpg';
import oxxoGasLogo from '../../../assets/img/fuel-prices/oxxogas-logo.png';
import petroilLogo from '../../../assets/img/fuel-prices/petroil-logo.jpg';
import imporLogo from '../../../assets/img/fuel-prices/impor-logo.jpg';
import primeBlueLogo from '../../../assets/img/fuel-prices/primeblue-logo.png';

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const getInitialStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 90);

  return formatInputDate(date);
};

const dayTranslations = {
  Monday: 'Lunes',
  Tuesday: 'Martes',
  Wednesday: 'Miércoles',
  Thursday: 'Jueves',
  Friday: 'Viernes',
  Saturday: 'Sábado',
  Sunday: 'Domingo',
};

const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  return `$${Number(value).toFixed(4)}`;
};

const formatAbsolutePrice = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  return formatPrice(Math.abs(Number(value)));
};

const latestPriceCellSx = {
  position: 'sticky',
  top: 128,
  height: 36,
  minHeight: 36,
  py: 0.5,
  backgroundColor: '#ffffff',
  color: '#0f172a',
  fontWeight: 900,
  borderRight: '1px solid #cbd5e1',
  borderBottom: '2px solid #cbd5e1',
  whiteSpace: 'nowrap',
  zIndex: 5,
};

const FuelGroupHeader = ({
  title,
  subtitle,
  logo = lovesLogo,
  logoAlt = "Love's Travel Stops",
  highlighted = false,
}) => (
  <Box
    sx={{
      minHeight: 82,
      mx: -2,
      my: -0.5,
      px: 1,
      py: 0.75,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
      backgroundColor: '#ffffff',
    }}
  >
    {logo && (
      <Box
        component="img"
        src={logo}
        alt={logoAlt}
        sx={{
          width: 'auto',
          height: 48,
          maxWidth: 120,
          objectFit: 'contain',
        }}
      />
    )}

    <Typography
      component="div"
      sx={{
        color: '#0f172a',
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1.2,
        textAlign: 'center',
        whiteSpace: 'normal',
      }}
    >
      {title}
    </Typography>

    {subtitle && (
      <Typography
        component="div"
        sx={{
          color: '#0f172a',
          fontSize: 12,
          fontWeight: 900,
          lineHeight: 1.1,
          textAlign: 'center',
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const additionalFuelColumns = [
  {
    id: 'opecBasket',
    header: () => (
      <FuelGroupHeader
        title="OPEC Basket"
        logo={opecLogo}
        logoAlt="OPEC"
      />
    ),
    columns: [
      {
        accessorKey: 'opec_basket',
        header: 'Petroleum',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'dieselMariposas',
    header: () => (
      <FuelGroupHeader
        title="Diesel Mariposas (SLP)"
        logo={gasomaxLogo}
        logoAlt="Gasomax"
      />
    ),
    columns: [
      {
        accessorKey: 'diesel_mariposas_liter',
        header: 'Litro Diesel',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'natgas',
    header: () => (
      <FuelGroupHeader
        title="Gas (NATGAS)"
        logo={natgasLogo}
        logoAlt="NATGAS"
      />
    ),
    columns: [
      {
        accessorKey: 'natgas_liter',
        header: 'Litro Líquido',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'cugas',
    header: () => (
      <FuelGroupHeader
        title="Gas (CUGAS)"
        logo={cugasLogo}
        logoAlt="CUGAS"
      />
    ),
    columns: [
      {
        accessorKey: 'cugas_liter',
        header: 'Litro Líquido',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'gnv',
    header: () => (
      <FuelGroupHeader
        title="Gas (Gran Tracto Pensión)"
        logo={gnvLogo}
        logoAlt="GNV"
      />
    ),
    columns: [
      {
        accessorKey: 'gnv_liter',
        header: 'Litro Líquido',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'erg',
    header: () => (
      <FuelGroupHeader
        title="ERG Multienergéticos"
        logo={ergLogo}
        logoAlt="ERG Multienergéticos"
      />
    ),
    columns: [
      {
        accessorKey: 'erg_liter',
        header: 'Litro Líquido',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'mxDieselSale',
    header: () => (
      <FuelGroupHeader
        title="MX Diesel (Venta)"
        logo={ebtLogo2}
        logoAlt="EBT"
      />
    ),
    columns: [
      {
        accessorKey: 'mx_diesel_sale_liter',
        header: 'Litro Diesel',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'dieselOxxoGas',
    header: () => (
      <FuelGroupHeader
        title="Diesel OXXO Gas"
        logo={oxxoGasLogo}
        logoAlt="OXXO Gas"
      />
    ),
    columns: [
      {
        accessorKey: 'diesel_oxxo_gas_liter',
        header: 'Litro Diesel',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'mxDieselCost',
    header: () => (
      <FuelGroupHeader
        title="MX Diesel (Costo)"
        logo={ebtLogo2}
        logoAlt="EBT"
      />
    ),
    columns: [
      {
        accessorKey: 'mx_diesel_cost_liter',
        header: 'Litro Diesel',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'mxDieselMonterreyCost',
    header: () => (
      <FuelGroupHeader
        title="MX Diesel Monterrey (Costo)"
        logo={petroilLogo}
        logoAlt="Petroil"
      />
    ),
    columns: [
      {
        accessorKey: 'mx_diesel_monterrey_cost_liter',
        header: 'Litro Diesel',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'ureaImporfil',
    header: () => (
      <FuelGroupHeader
        title="Urea Imporfil"
        logo={imporLogo}
        logoAlt="Imporfil"
      />
    ),
    columns: [
      {
        accessorKey: 'urea_imporfil_def_gallon',
        header: 'DEF Galón',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'ureaPrimeBlueMx',
    header: () => (
      <FuelGroupHeader
        title="Urea Prime Blue (MX)"
        logo={primeBlueLogo}
        logoAlt="Prime Blue México"
      />
    ),
    columns: [
      {
        accessorKey: 'urea_prime_blue_mx_def_gallon',
        header: 'DEF Galón',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
  {
    id: 'ureaPrimeBlueUsa',
    header: () => (
      <FuelGroupHeader
        title="Urea Prime Blue (USA)"
        logo={primeBlueLogo}
        logoAlt="Prime Blue USA"
      />
    ),
    columns: [
      {
        accessorKey: 'urea_prime_blue_usa_def_gallon',
        header: 'DEF Galón',
        cell: ({ getValue }) => formatPrice(getValue()),
      },
    ],
  },
];

const additionalLatestPriceKeys = [
  'opec_basket',
  'diesel_mariposas_liter',
  'natgas_liter',
  'cugas_liter',
  'gnv_liter',
  'erg_liter',
  'mx_diesel_sale_liter',
  'diesel_oxxo_gas_liter',
  'mx_diesel_cost_liter',
  'mx_diesel_monterrey_cost_liter',
  'urea_imporfil_def_gallon',
  'urea_prime_blue_mx_def_gallon',
  'urea_prime_blue_usa_def_gallon',
];

const DailyFuelPrice = () => {
  const [data, setData] = useState([]);
  const [latestPrice, setLatestPrice] = useState(null);
  const [startDate, setStartDate] = useState(getInitialStartDate);
  const [endDate, setEndDate] = useState(() =>
    formatInputDate(new Date())
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const columns = useMemo(
    () => [
      {
        id: 'calendar',
        //header: 'Calendario',
        header: 'Calendar',
        columns: [
          {
            accessorKey: 'week_number',
            //header: 'Semana',
            header: 'Week #',
            cell: ({ row }) => (
              <Box>
                <Typography
                  component="div"
                  sx={{ fontWeight: 800, fontSize: 16 }}
                >
                  {row.original.week_number}
                </Typography>

                <Typography
                  component="div"
                  sx={{ color: '#64748b', fontSize: 11 }}
                >
                  {row.original.year_number}
                </Typography>
              </Box>
            ),
          },
/*           {
            accessorKey: 'day_name',
            header: 'Día',
            cell: ({ getValue }) =>
              dayTranslations[getValue()] || getValue(),
          }, */
          {
            accessorKey: 'day_name',
            header: 'Day of Week',
           },
          {
            accessorKey: 'effective_date',
            //header: 'Fecha',
            header: 'Date',
            cell: ({ getValue }) => {
                const [year, month, day] = getValue().split('-');

                return `${month}/${day}/${year}`;
            },
           },
        ],
      },
      {
        id: 'arguindegui',
        header: () => (
          <FuelGroupHeader
            title="Arguindegui Oil Com (Tower Yard)"
            logo={arguindeguiLogo}
            logoAlt="Arguindegui Oil Companies"
          />
        ),
        columns: [
          {
            accessorKey: 'arguindegui_diesel',
            header: 'Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
          {
            accessorKey: 'arguindegui_red_diesel',
            header: 'Red Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
          {
            accessorKey: 'arguindegui_def',
            header: 'DEF',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      {
        id: 'pilot377',
        header: () => (
          <FuelGroupHeader
            title="Pilot Travel Center 377 (I-35 Exit 13)"
            logo={pilotFlyingJLogo}
            logoAlt="Pilot Travel Center"
          />
        ),
        columns: [
          {
            accessorKey: 'pilot_377_diesel',
            header: 'Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
          {
            accessorKey: 'pilot_377_def',
            header: 'DEF',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      {
        id: 'flyingJ730',
        header: () => (
          <FuelGroupHeader
            title="Flying J Travel Plaza 730 (I-35 Exit 13)"
            logo={pilotFlyingJLogo}
            logoAlt="Flying J Travel Center"
          />
        ),
        columns: [
          {
            accessorKey: 'flying_j_730_diesel',
            header: 'Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
          {
            accessorKey: 'flying_j_730_def',
            header: 'DEF',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      {
        id: 'taLaredo',
        header: () => (
          <FuelGroupHeader
            title="TA Laredo Henry Albert Travel Center #0153 (I-35 Exit 13)"
            logo={taLogo}
            logoAlt="TravelCenters of America"
          />
        ),
        columns: [
          {
            accessorKey: 'ta_laredo_diesel',
            header: 'Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
          {
            accessorKey: 'ta_laredo_def',
            header: 'DEF',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      {
        id: 'laredo',
        //header: "Love's Laredo, TX",
        //header: "Love's 935 (Laredo, TX) EBT PRICE",
        header: () => (
            <FuelGroupHeader title="Love's 935 (Laredo, TX)" subtitle="EBT PRICE" />
        ),
        columns: [
          {
            accessorKey: 'laredo_diesel',
            header: 'Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
          {
            accessorKey: 'laredo_def',
            header: 'DEF',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      {
        id: 'natalia',
        //header: "Love's Natalia, TX",
        //header: "Love's (Natalia, TX) EBT PRICE",
        header: () => (
            <FuelGroupHeader title="Love's (Natalia, TX)" subtitle="EBT PRICE" />
        ),
        columns: [
          {
            accessorKey: 'natalia_diesel',
            header: 'Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
          {
            accessorKey: 'natalia_def',
            header: 'DEF',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      {
        id: 'eia',
        header: () => (
          <FuelGroupHeader
            title="U.S. Energy Information Administration"
            logo={eiaLogo}
            logoAlt="U.S. Energy Information Administration"
          />
        ),
        columns: [
          {
            accessorKey: 'eia_diesel_price',
            header: 'Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      {
        id: 'eiaPricePerMile',
        header: () => (
          <FuelGroupHeader
            title="Price per mile"
            logo={eiaLogo}
            logoAlt="U.S. Energy Information Administration"
          />
        ),
        columns: [
          {
            accessorKey: 'eia_weekly_change',
            header: 'Diesel',
            cell: ({ getValue }) => formatAbsolutePrice(getValue()),
          },
        ],
      },
      {
        id: 'lovesNationalAverage',
        header: () => (
          <FuelGroupHeader 
            title="National Average price per gallon" 
          />
        ),
        columns: [
          {
            accessorKey: 'loves_national_avg',
            header: 'Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      {
        id: 'lovesDiscountAverage',
        header: () => (
          <FuelGroupHeader 
            title="Average Price per gallon with discount" 
            logo={ebtLogo} 
          />
        ),
        columns: [
          {
            accessorKey: 'loves_discount_avg',
            header: 'Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      {
        id: 'lovesRealEbt',
        header: () => (
          <FuelGroupHeader 
            title="Average price por gallon real EBT" 
            logo={ebtLogo} 
          />
        ),
        columns: [
          {
            accessorKey: 'loves_real_ebt_yesterday',
            header: 'Diesel',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      {
        id: 'arguindeguiInternalPrice',
        header: () => (
          <FuelGroupHeader
            title="Arguindegui Diesel Price"
            logo={arguindeguiLogo}
            logoAlt="Arguindegui Oil Companies"
          />
        ),
        columns: [
          {
            accessorKey: 'arguindegui_clear_diesel_price',
            header: 'Clear Diesel (ULS)',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
          {
            accessorKey: 'arguindegui_red_diesel_price',
            header: 'Red Diesel (ULSDD)',
            cell: ({ getValue }) => formatPrice(getValue()),
          },
        ],
      },
      ...additionalFuelColumns,
    ],
    []
  );

 const weekRowSpans = useMemo(() => {
  const spans = {};

  data.forEach((item, index) => {
    const currentKey = `${item.year_number}-${item.week_number}`;

    const previousItem = data[index - 1];
    const previousKey = previousItem
      ? `${previousItem.year_number}-${previousItem.week_number}`
      : null;

    if (currentKey === previousKey) {
      return;
    }

    let rowSpan = 1;

    for (let nextIndex = index + 1; nextIndex < data.length; nextIndex += 1) {
      const nextItem = data[nextIndex];
      const nextKey = `${nextItem.year_number}-${nextItem.week_number}`;

      if (nextKey !== currentKey) {
        break;
      }

      rowSpan += 1;
    }

    spans[index] = rowSpan;
  });

  return spans;
}, [data]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const fetchPrices = useCallback(async () => {
    if (!startDate || !endDate) {
    //   setError('Selecciona una fecha inicial y una fecha final.');
      setError('Select a start date and an end date.');
      return;
    }

    if (startDate > endDate) {
      //setError('La fecha inicial no puede ser mayor a la fecha final.');
      setError('The start date cannot be later than the end date.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await myApi.get('/daily_fuel_price', {
        params: {
          startDate,
          endDate,
        },
      });

      if (response.data?.success) {
        setData(response.data.data || []);
        setLatestPrice(response.data.latest_prices || null);
      } else {
        setData([]);
        setLatestPrice(null);
        //setError('No fue posible obtener los precios.');
        setError('Unable to retrieve fuel prices.');
      }
    } catch (requestError) {
      console.error(requestError);
      setData([]);
      setLatestPrice(null);
      //setError('Ocurrió un error al consultar los precios.');
      setError('An error occurred while retrieving fuel prices.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        p: { xs: 2, md: 3 },
        backgroundColor: '#f8fafc',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: '1px solid #e2e8f0',
          borderRadius: 3,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              EBT Daily Fuel Price
            </Typography>

            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {/* Precios diarios de combustible */}
              Daily fuel prices
            </Typography>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
          >
            <TextField
              //label="Fecha inicial"
              label="Start date"
              type="date"
              size="small"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />

            <TextField
              //label="Fecha final"
              label="End date"
              type="date"
              size="small"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />

            <Button
              variant="contained"
              startIcon={
                loading
                  ? <CircularProgress size={17} color="inherit" />
                  : <RefreshRoundedIcon />
              }
              disabled={loading}
              onClick={fetchPrices}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              {/* Actualizar */}
              Refresh
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 285px)',
            border: '1px solid #cbd5e1',
            borderRadius: 2,
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              width: 4850,
              minWidth: 4850,
              tableLayout: 'fixed',
            }}
          >
            <TableHead>
              {table.getHeaderGroups().map((headerGroup, groupIndex) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      colSpan={header.colSpan}
                      align="center"
                      sx={{
                        position: 'sticky',
                        top: groupIndex === 0 ? 0 : 92,
                        height: groupIndex === 0 ? 92 : 46,
                        minHeight: groupIndex === 0 ? 92 : 46,
                        py: 0.5,

                        backgroundColor:
                            groupIndex === 0 ? '#ffffff' : '#eef4fa',

                        color: '#0f172a',

                        fontWeight: 800,
                        borderRight: '1px solid #cbd5e1',
                        borderBottom: '1px solid #cbd5e1',
                        whiteSpace: 'normal',
                        lineHeight: 1.15,
                        fontSize: groupIndex === 0 ? 11 : 12,
                        zIndex: groupIndex === 0 ? 6 : 5,
                     }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              <TableRow>
                <TableCell
                    colSpan={3}
                    align="center"
                    sx={{
                    position: 'sticky',
                    top: 138,
                    height: 36,
                    minHeight: 36,
                    py: 0.5,
                    backgroundColor: '#f8fafc',
                    color: '#0f172a',
                    fontWeight: 900,
                    borderRight: '2px solid #cbd5e1',
                    borderBottom: '2px solid #cbd5e1',
                    whiteSpace: 'nowrap',
                    zIndex: 5,
                    }}
                >
                    Fuel Price per Gal.
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.arguindegui_diesel)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.arguindegui_red_diesel)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.arguindegui_def)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.pilot_377_diesel)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.pilot_377_def)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.flying_j_730_diesel)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.flying_j_730_def)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.ta_laredo_diesel)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.ta_laredo_def)}
                </TableCell>

                <TableCell
                    align="right"
                    sx={latestPriceCellSx}
                >
                    {formatPrice(latestPrice?.laredo_diesel)}
                </TableCell>

                <TableCell
                    align="right"
                    sx={latestPriceCellSx}
                >
                    {formatPrice(latestPrice?.laredo_def)}
                </TableCell>

                <TableCell
                    align="right"
                    sx={latestPriceCellSx}
                >
                    {formatPrice(latestPrice?.natalia_diesel)}
                </TableCell>

                <TableCell
                    align="right"
                    sx={latestPriceCellSx}
                >
                    {formatPrice(latestPrice?.natalia_def)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.eia_diesel_price)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={latestPriceCellSx}
                >
                  {formatAbsolutePrice(latestPrice?.eia_weekly_change)}
                </TableCell>

                <TableCell
                    align="right"
                    sx={latestPriceCellSx}
                >
                    {formatPrice(latestPrice?.loves_national_avg)}
                </TableCell>

                <TableCell 
                    align="right" 
                    sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.loves_discount_avg)}
                </TableCell>

                <TableCell 
                    align="right" 
                    sx={latestPriceCellSx}
                >
                  {formatPrice(latestPrice?.loves_real_ebt_yesterday)}
                </TableCell>

                <TableCell align="right" sx={latestPriceCellSx}>
                  {formatPrice(
                    latestPrice?.arguindegui_clear_diesel_price
                  )}
                </TableCell>

                <TableCell align="right" sx={latestPriceCellSx}>
                  {formatPrice(
                    latestPrice?.arguindegui_red_diesel_price
                  )}
                </TableCell>

                {additionalLatestPriceKeys.map((key) => (
                  <TableCell
                    key={key}
                    align="right"
                    sx={latestPriceCellSx}
                  >
                    {formatPrice(latestPrice?.[key])}
                  </TableCell>
                ))}
                </TableRow>
            </TableHead>

            <TableBody>
              {table.getRowModel().rows.map((row) => {
                const weekRowSpan = weekRowSpans[row.index];
                const isFirstRowOfWeek = Boolean(weekRowSpan);
                const visibleCells = row.getVisibleCells();

                return (
                    <TableRow
                    key={row.id}
                    hover
                    sx={{
                        backgroundColor: isFirstRowOfWeek
                        ? '#f0f7ff'
                        : '#ffffff',

                        '&:hover': {
                        backgroundColor: '#eaf3ff',
                        },

                        '& td': {
                        borderTop: isFirstRowOfWeek
                            ? '2px solid #93c5fd'
                            : undefined,
                        },
                    }}
                    >
                    {weekRowSpan && (
                        <TableCell
                        rowSpan={weekRowSpan}
                        align="center"
                        sx={{
                            minWidth: 100,
                            backgroundColor: '#edf3f9',
                            borderLeft: '4px solid #cbd5e1',
                            borderRight: '1px solid #cbd5e1',
                            borderTop: '2px solid #cbd5e1',
                            verticalAlign: 'middle',
                        }}
                        >
                        <Typography
                            component="div"
                            sx={{
                            color: '#0f172a',
                            fontSize: 30,
                            fontWeight: 900,
                            lineHeight: 1,
                            }}
                        >
                            {row.original.week_number}
                        </Typography>

                        <Typography
                            component="div"
                            sx={{
                            mt: 0.75,
                            color: '#64748b',
                            fontSize: 12,
                            fontWeight: 700,
                            }}
                        >
                            {row.original.year_number}
                        </Typography>
                        </TableCell>
                    )}

                    {visibleCells.slice(1).map((cell, cellIndex) => (
                        <TableCell
                        key={cell.id}
                        align={cellIndex < 2 ? 'center' : 'right'}
                        sx={{
                            height: 38,
                            px: 2,
                            color: '#0f172a',
                            fontSize: 13,
                            fontWeight: cellIndex < 2 ? 700 : 600,
                            borderRight: '1px solid #cbd5e1',
                            borderBottom: '1px solid #cbd5e1',
                            whiteSpace: 'nowrap',
                        }}
                        >
                        {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                        )}
                        </TableCell>
                    ))}
                    </TableRow>
                );
                })}

              {!loading && data.length === 0 && !error && (
                <TableRow>
                  <TableCell colSpan={36} align="center" sx={{ py: 6 }}>
                    {/* No hay precios para el periodo seleccionado. */}
                    No prices are available for the selected period.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DailyFuelPrice;